import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
} from '@mui/material'
import { Close, ExpandMore, Download } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { apiClient } from '../../services/api'

interface CatalogueExportDialogProps {
  open: boolean
  catalogue: {
    id: string
    name: string
  }
  onClose: () => void
}

type PricingTier = 'sale_price' | 'wholesale_price'
type ExportFormat = 'pdf' | 'xlsx'

interface ProductNameOverride {
  product_id: string
  product_name: string
  custom_name: string
}

interface CatalogueProduct {
  id: string
  name: string
  sku: string
  sale_price: number
  wholesale_price: number
}

const CatalogueExportDialog: React.FC<CatalogueExportDialogProps> = ({
  open,
  catalogue,
  onClose,
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const [pricingTier, setPricingTier] = useState<PricingTier>('sale_price')
  const [format, setFormat] = useState<ExportFormat>('pdf')
  const [customNames, setCustomNames] = useState<Record<string, string>>({})
  const [products, setProducts] = useState<CatalogueProduct[]>([])
  const [exporting, setExporting] = useState(false)
  const [loadingProducts, setLoadingProducts] = useState(false)

  // Load catalogue products when dialog opens
  useEffect(() => {
    if (open && catalogue.id) {
      loadCatalogueProducts()
    }
  }, [open, catalogue.id])

  const loadCatalogueProducts = async () => {
    setLoadingProducts(true)
    try {
      const response = await apiClient.get(`/admin/catalogues/${catalogue.id}/products`, {
        params: { per_page: 1000 } // Load all products for export
      })
      setProducts(response.data.items || [])
    } catch (error: any) {
      console.error('Error loading catalogue products:', error)
      enqueueSnackbar(
        error?.response?.data?.detail || 'Failed to load catalogue products',
        { variant: 'error' }
      )
    } finally {
      setLoadingProducts(false)
    }
  }

  const handleExport = async () => {
    setExporting(true)
    try {
      // Prepare custom product names array
      const productNameOverrides: ProductNameOverride[] = Object.entries(customNames)
        .filter(([_, customName]) => customName.trim() !== '')
        .map(([productId, customName]) => {
          const product = products.find(p => p.id === productId)
          return {
            product_id: productId,
            product_name: product?.name || '',
            custom_name: customName.trim()
          }
        })

      const response = await apiClient.post(
        `/admin/catalogues/${catalogue.id}/export`,
        {
          pricing_tier: pricingTier,
          format: format,
          custom_product_names: productNameOverrides,
          include_images: true,
        },
        {
          responseType: 'blob', // Important for file download
        }
      )

      // Create download link
      const blob = new Blob([response.data], {
        type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `catalogue_${catalogue.name.replace(/\s+/g, '_')}_${pricingTier}.${format}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      enqueueSnackbar('Catalogue exported successfully', { variant: 'success' })
      onClose()
    } catch (error: any) {
      console.error('Error exporting catalogue:', error)
      enqueueSnackbar(
        error?.response?.data?.detail || 'Failed to export catalogue',
        { variant: 'error' }
      )
    } finally {
      setExporting(false)
    }
  }

  const handleClose = () => {
    if (!exporting) {
      setCustomNames({})
      onClose()
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      {exporting && <LinearProgress />}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#2196f3',
          color: 'white',
        }}
      >
        <Box>
          <Typography variant="h6">Export Catalogue</Typography>
          <Typography variant="caption">{catalogue.name}</Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'inherit' }} disabled={exporting}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {loadingProducts ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              Export this catalogue with custom pricing and product names for different customer segments.
            </Alert>

            <FormControl fullWidth margin="normal">
              <InputLabel>Pricing Tier</InputLabel>
              <Select
                value={pricingTier}
                onChange={(e) => setPricingTier(e.target.value as PricingTier)}
                label="Pricing Tier"
              >
                <MenuItem value="sale_price">
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Retail Price (sale_price)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      For retail customers and general pricing
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="wholesale_price">
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Wholesale Price (wholesale_price)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      For wholesale customers and bulk orders
                    </Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Export Format</InputLabel>
              <Select
                value={format}
                onChange={(e) => setFormat(e.target.value as ExportFormat)}
                label="Export Format"
              >
                <MenuItem value="pdf">
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      PDF (for customers)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Professional catalogue document with images
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="xlsx">
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Excel (for editing)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Spreadsheet format for further customization
                    </Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {products.length > 0 && (
              <Accordion sx={{ mt: 3 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">
                    Custom Product Names (Optional)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Override product names for this export. Leave empty to use original names.
                  </Typography>
                  <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                    {products.map((product) => (
                      <TextField
                        key={product.id}
                        fullWidth
                        margin="dense"
                        label={product.name}
                        placeholder="Leave empty to use original name"
                        value={customNames[product.id] || ''}
                        onChange={(e) =>
                          setCustomNames((prev) => ({
                            ...prev,
                            [product.id]: e.target.value,
                          }))
                        }
                        helperText={`SKU: ${product.sku} | ${pricingTier === 'sale_price' ? `Sale: ₹${product.sale_price}` : `Wholesale: ₹${product.wholesale_price}`}`}
                        size="small"
                      />
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {products.length === 0 && !loadingProducts && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This catalogue has no products. Please add products before exporting.
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={exporting}>
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          startIcon={exporting ? <CircularProgress size={20} /> : <Download />}
          disabled={exporting || loadingProducts || products.length === 0}
          sx={{
            bgcolor: '#4caf50',
            '&:hover': { bgcolor: '#388e3c' },
          }}
        >
          {exporting ? 'Exporting...' : `Export as ${format.toUpperCase()}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CatalogueExportDialog
